import { useQueryClient } from "@tanstack/react-query";
import type { AnyRouter } from "@tanstack/react-router";
import type { User } from "@yalana/contracts";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from "react";

import { getToken } from "../../shared/infrastructure/api-client";
import { customersQueries } from "../../shared/queries/customers.queries";
import { authQueries } from "./api/auth.queries";
import { composeAuthValue, readCache, writeCache } from "./auth-helpers";
import { authReducer } from "./auth-reducer";
import { AuthContext, useAuth } from "./auth-context";
import { useAbility, StubAuthProvider } from "./stub-auth";

export { useAuth, useAbility, StubAuthProvider };

const FOCUS_SYNC_INTERVAL_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Orchestrator: AuthProvider
// ---------------------------------------------------------------------------
export function AuthProvider({
  children,
  router,
}: {
  children: ReactNode;
  router: AnyRouter;
}) {
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(authReducer, {
    user: readCache().user,
    authorizedTenants: [],
    loading: true,
  });
  const lastFocusSyncRef = useRef(0);

  const syncUser = useCallback(
    async (hasSession: boolean): Promise<User | null> => {
      let appUser: User | null = null;

      if (hasSession) {
        try {
          appUser = await queryClient.fetchQuery(authQueries.me());
        } catch (err) {
          console.error("Failed to fetch current user from API:", err);
        }
      }

      if (appUser) {
        writeCache(appUser);

        try {
          const allCustomers = await queryClient.fetchQuery(
            customersQueries.list(),
          );
          const authorized = allCustomers.filter((t) =>
            appUser.customerIds.includes(t.id),
          );

          dispatch({
            type: "INITIALIZE",
            payload: {
              user: appUser,
              authorizedTenants: authorized,
            },
          });
        } catch (err) {
          console.error("Failed to fetch user customers:", err);
          dispatch({
            type: "INITIALIZE",
            payload: {
              user: appUser,
              authorizedTenants: [],
            },
          });
        }
      } else {
        writeCache(null);
        dispatch({ type: "LOGOUT" });
      }

      return appUser;
    },
    [queryClient],
  );

  useEffect(() => {
    let isMounted = true;

    async function init() {
      try {
        const token = await getToken();
        if (!isMounted) return;
        await syncUser(!!token);
      } catch (err) {
        console.error("Auth init() error:", err);
        if (isMounted) await syncUser(false);
      }
    }

    void init();

    function onFocus() {
      if (!isMounted) return;
      const now = Date.now();
      if (now - lastFocusSyncRef.current < FOCUS_SYNC_INTERVAL_MS) return;
      lastFocusSyncRef.current = now;
      // Do NOT call invalidateToken() here — it races with React Query's
      // refetchOnWindowFocus: concurrent queries would call getToken() while
      // the inflight authClient.getSession() is in flight, and on mobile the
      // session fetch can briefly fail (network resuming from background),
      // returning null → requests go without Bearer → 401 → redirect loop.
      // The token TTL (5 min) already handles freshness; syncUser detects
      // real session expiry via /me (skipRedirect: true).
      void getToken().then((token) => {
        if (!isMounted) return;
        void syncUser(!!token);
      });
    }

    window.addEventListener("focus", onFocus);
    return () => {
      isMounted = false;
      window.removeEventListener("focus", onFocus);
    };
  }, [syncUser]);

  // Sync auth state to router context for use in beforeLoad hooks.
  if (
    router.options?.context &&
    (router.options.context.auth?.user !== state.user ||
      router.options.context.auth?.loading !== state.loading)
  ) {
    router.update({
      context: {
        ...router.options.context,
        auth: { user: state.user, loading: state.loading },
      },
    });
  }

  const value = composeAuthValue(
    state.user,
    state.authorizedTenants,
    state.loading,
    syncUser,
    queryClient,
  );

  // Block RouterProvider while auth is initializing — even with a cached user.
  // Rendering routes with loading:true + stale-cached user causes $customer
  // beforeLoad to fire and welcome-page queries to run before getToken() has
  // resolved. On mobile, if the session fetch fails those queries go without
  // Bearer → 401 → window.location redirect → reload → cached user still
  // present → infinite loop. Waiting for init() ensures getToken() has already
  // resolved and the token is cached before any query fires.
  if (state.loading) {
    return null;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
